/* eslint-disable no-undef */
/* eslint-disable react/prop-types */
import { useEffect, useState, useMemo } from 'react';
import { Modal } from 'antd';
import { useSelector } from 'react-redux';
import {
  FileTextOutlined,
  LinkOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  createPatientDocument,
  updatePatientDocument,
  unlinkDocumentFromEncounter,
  unlinkDocumentFromAppointment,
  getEncounterDocuments,
  getAppointmentDocuments,
} from './service';
import { UpgradeTooltip } from '../../../ambient_solo/components/Payments/UpgradeTooltip';
import { LockIcon } from 'src/icons/Lock.icon';
import { PatientNotesModal } from './PatientNotesModal';
import FileDocument from './FileDocument';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
import EncounterSelectionModal from './EncounterSelectionModal';
import { mergePatientTextDocuments } from 'src/utils/encounter';

const tempDocumentIdPrefix = '__temp__';
const maxAllowedUploadSize = 400000;

function PatientNotes({
  schedulepage,
  isEnabled,
  source = 'pre_chart_history',
  patientDetails,
  encounter,
  currentPractitioner,
  settingsOrgValue,
}) {
  const patientName =
    encounter?.patient?.first_name + ' ' + encounter?.patient?.last_name;
  const [isLoading, setIsLoading] = useState(false);
  const { encounterDetails } = useSelector((state) => state?.encounterState);
  const { getUuid } = useSelector((state) => state?.appointmentState);
  const { user } = useAuth();

  const encounterId = encounterDetails?.encounter_id;
  const appointmentUuid = getUuid?.appt_uuid;

  const [notesDocument, setNotesDocument] = useState({
    id: null,
    content: '',
  });
  const [attachments, setAttachments] = useState([]);
  const [attachmentsModal, setAttachmentsModal] = useState(false);
  const [isAllDocumentDeleted, setIsAllDocumentDeleted] = useState(false);
  const [isEncounterModalOpen, setIsEncounterModalOpen] = useState(false);

  const createFileDocument = async (file) => {
    const fileObj = file.originFileObj;
    setIsLoading(true);
    try {
      setAttachments([
        ...attachments,
        {
          document: {
            patient_document_id: `${tempDocumentIdPrefix}${Date.now()}`,
            name: fileObj.name,
            type: 'file',
            mime_type: fileObj.type,
            size: fileObj.size,
          },
          fileToUpload: fileObj,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const updateNotesDocument = async (content) => {
    if (!notesDocument.id) {
      if (!content) {
        return;
      }
      const document = await createPatientDocument(
        encounterId,
        appointmentUuid,
        {
          content,
          type: 'text',
          source,
        }
      );
      setNotesDocument((prev) => ({
        ...prev,
        id: document.patient_document_id,
      }));
    } else {
      await updatePatientDocument(notesDocument.id, {
        content,
      });
    }
  };

  const onUpdate = async (index, updatedAttachment) => {
    setAttachments(
      attachments.map((attachment, i) =>
        i === index ? { ...attachment, ...updatedAttachment } : attachment
      )
    );
  };

  const deleteDocument = async (attachment, source) => {
    const documentId = attachment.document.patient_document_id;
    if (documentId?.includes(tempDocumentIdPrefix)) {
      setAttachments(
        attachments.filter(
          ({ document }) => document.patient_document_id !== documentId
        )
      );
    } else {
      Modal.confirm({
        centered: true,
        title: 'Delete patient document?',
        okType: 'default',
        okButtonProps: {
          className:
            'border border-[#d63232] bg-[#d63232] text-white text-[14px]',
        },
        onOk: async () => {
          if (source === 'pre_chart_history') {
            await unlinkDocumentFromEncounter(encounterId, [documentId]);
          } else if (source === 'vca_patient_history') {
            await unlinkDocumentFromAppointment(appointmentUuid, [documentId]);
          }
          setAttachments(
            attachments.filter(
              ({ document }) => document.patient_document_id !== documentId
            )
          );
        },
      });
    }
  };

  const deleteMultipleDocuments = async (attachment, source) => {
    const documentId = attachment.document.patient_document_id;
    if (documentId?.includes(tempDocumentIdPrefix)) {
      setAttachments(
        attachments.filter(
          ({ document }) => document.patient_document_id !== documentId
        )
      );
    } else {
      if (source === 'pre_chart_history') {
        await unlinkDocumentFromEncounter(encounterId, [documentId]);
      } else if (source === 'vca_patient_history') {
        await unlinkDocumentFromAppointment(appointmentUuid, [documentId]);
      }
    }
  };
  const handleDeleteMultipleDocs = async (attachments, source) => {
    Modal.confirm({
      centered: true,
      title: 'Delete all patient document(s)?',
      okType: 'default',
      okButtonProps: {
        className:
          'border border-[#d63232] bg-[#d63232] text-white  text-[14px]',
      },
      onOk: async () => {
        await Promise.all(
          attachments.map((attachment) =>
            deleteMultipleDocuments(attachment, source)
          )
        );

        await updateNotesDocument(null);
        setIsAllDocumentDeleted(true);
        setAttachmentsModal(false);
      },
    });
  };

  const currentTotalUploadedSize = useMemo(() => {
    return attachments.reduce((acc, { document }) => {
      return acc + (document.size || 0);
    }, 0);
  }, [attachments]);

  const maxAllowedNotesLength = Math.floor(
    (maxAllowedUploadSize - currentTotalUploadedSize) / 4
  );

  const validateFile = async (file) => {
    const fileObj = file.originFileObj;

    const newTotalHistorySize =
      currentTotalUploadedSize +
      (notesDocument.content?.length || 0) * 4 +
      fileObj.size;
    if (newTotalHistorySize > maxAllowedUploadSize) {
      analytics.track(
        `Upload limit exceeded In ${
          source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
        } History Modal`,
        {
          schedulepage,
          totalUploadedFileSize: newTotalHistorySize,
          email: user?.email,
        }
      );
      Modal.warning({
        centered: true,
        title: 'Upload limit exceeded',
        content: `The total size of all files uploaded should not exceed ${
          maxAllowedUploadSize / 1000
        }KB.`,
      });
      return false;
    }

    // check if file is already in attachments
    const similarFileExists = attachments.some(
      (attachment) =>
        attachment.document.name === fileObj.name &&
        attachment.document.mime_type === fileObj.type &&
        attachment.document.size === fileObj.size
    );
    if (similarFileExists) {
      return await new Promise((resolve) => {
        Modal.confirm({
          centered: true,
          title: 'Similar file already attached',
          content: 'Do you still want to upload?',
          okText: 'Proceed',
          cancelText: 'Cancel',
          onOk: () => {
            analytics.track(
              `Clicked Proceed On Duplicate File Alert In ${
                source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
              } History Modal`,
              {
                schedulepage,
                email: user?.email,
              }
            );
            resolve(true);
          },
          onCancel: () => {
            analytics.track(
              `Clicked Cancel On Duplicate File Alert In ${
                source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
              } History Modal`,
              {
                schedulepage,
                email: user?.email,
              }
            );
            resolve(false);
          },
        });
      });
    }

    return true;
  };

  const renderDocument = (attachment, index) => {
    return (
      <FileDocument
        key={attachment.document.patient_document_id}
        {...attachment}
        source={source}
        schedulepage={schedulepage}
        encounterId={encounterId}
        onUpdate={(updatedAttachment) => onUpdate(index, updatedAttachment)}
        onDelete={() => {
          deleteDocument(attachment, source);
        }}
      />
    );
  };

  useEffect(() => {
    if (!appointmentUuid || source !== 'vca_patient_history') {
      return;
    }
    setIsLoading(true);
    getAppointmentDocuments(appointmentUuid)
      .then((documents) => {
        const { notesDocument, attachments } =
          mergePatientTextDocuments(documents);
        setAttachments(
          attachments.map((attachment) => ({
            document: attachment,
          }))
        );
        setNotesDocument(notesDocument);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [appointmentUuid, source]);

  useEffect(() => {
    if (!encounterId || source !== 'pre_chart_history') {
      return;
    }
    setIsLoading(true);
    getEncounterDocuments(encounterId)
      .then((documents) => {
        const { notesDocument, attachments } =
          mergePatientTextDocuments(documents);
        setAttachments(
          attachments.map((attachment) => ({
            document: attachment,
          }))
        );
        setNotesDocument(notesDocument);
      })
      .finally(() => {
        setIsLoading(false);
        setIsAllDocumentDeleted(false);
      });
  }, [encounterId, isAllDocumentDeleted, source]);

  if (!isEnabled) {
    return (
      <UpgradeTooltip className="flex space-x-4 mt-4 justify-center">
        <button
          type={'button'}
          className="w-full ih-button-secondary justify-center gap-2"
          disabled
        >
          <LockIcon />
          {source === 'vca_patient_history' ? 'Add VCA history' : 'Add history'}
        </button>
      </UpgradeTooltip>
    );
  }

  let trigger = null;
  if (!attachments.length && !notesDocument.content) {
    trigger = (
      <div
        className={`flex ${
          source === 'pre_chart_history' ? 'mt-4' : ''
        } space-x-4 justify-center`}
      >
        <button
          disabled={isLoading}
          type={'button'}
          className="w-full ih-button-secondary justify-center gap-2"
          onClick={(e) => {
            e.preventDefault();
            analytics.track(
              `Clicked Add ${
                source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
              } History`,
              {
                schedulepage,
                email: user?.email,
                ehr_appointment_id:
                  !!encounter?.encounter_ehr_data?.appointment_ehr_id,
              }
            );
            if (
              encounter?.encounter_ehr_data?.appointment_ehr_id &&
              settingsOrgValue?.org_settings?.ehr_precharting_enabled
            ) {
              setIsEncounterModalOpen(true);
            } else {
              setAttachmentsModal(true);
            }
          }}
        >
          {isLoading ? <LoadingOutlined /> : <FileTextOutlined />}
          {source === 'vca_patient_history' ? 'Add VCA history' : 'Add history'}
        </button>
      </div>
    );
  } else {
    trigger = (
      <div
        className={`${
          source === 'pre_chart_history' ? 'mt-4' : ''
        } w-full flex justify-center`}
      >
        <div className="text-[#667085] text-center p-2 flex align-center">
          <span
            className="flex text-sm gap-2 cursor-pointer"
            onClick={() => {
              analytics.track(
                `Clicked Linked Attachments In ${
                  source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                } History`,
                {
                  schedulepage,
                  email: user?.email,
                }
              );
              setAttachmentsModal(true);
            }}
          >
            <LinkOutlined />
            {source === 'vca_patient_history' ? 'VCA History' : 'History'}{' '}
            {[
              notesDocument.content ? 'notes' : null,
              attachments.length ? `${attachments.length} file(s)` : null,
            ]
              .filter(Boolean)
              .join(' and ')}{' '}
            attached
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      {trigger}
      {attachmentsModal && (
        <PatientNotesModal
          patientDetails={patientDetails}
          setAttachmentsModal={setAttachmentsModal}
          isLoading={isLoading}
          schedulepage={schedulepage}
          notesDocument={notesDocument}
          setNotesDocument={setNotesDocument}
          maxAllowedNotesLength={maxAllowedNotesLength}
          updateNotesDocument={updateNotesDocument}
          attachments={attachments}
          renderDocument={renderDocument}
          createFileDocument={createFileDocument}
          validateFile={validateFile}
          source={source}
          isAllDocumentDeleted={isAllDocumentDeleted}
          handleDeleteMultipleDocs={handleDeleteMultipleDocs}
        />
      )}
      {encounter?.encounter_ehr_data?.appointment_ehr_id &&
        settingsOrgValue?.org_settings?.ehr_precharting_enabled && (
          <EncounterSelectionModal
            isOpen={isEncounterModalOpen}
            onClose={() => setIsEncounterModalOpen(false)}
            patientId={
              patientDetails?.patient_id || encounter?.patient?.patient_id
            }
            practitionerId={currentPractitioner?.practitioner_id}
            patientName={patientName}
            practitionerName={currentPractitioner?.practitioner_name}
            encounterId={encounterId}
            parent_appointment_ehr_id={
              encounter?.encounter_ehr_data?.appointment_ehr_id
            }
            appointmentId={encounter?.encounter_ehr_data?.appointment_id}
          />
        )}
    </>
  );
}

export { PatientNotes };
